import { audio, div, image } from "../../../lib/markup/tags";
import {
  INSTRUCTIONS_AUDIO_ID,
  INSTRUCTIONS_IMAGE_ID,
  getSelectedKeyboardLayoutConfig,
} from "../../../lib/utils";

function resolveInstructionLanguage(layoutConfig) {
  const layoutId = String(layoutConfig?.id ?? "").toUpperCase();
  const localeToken = layoutId.split("_").pop() ?? "";
  if (localeToken.startsWith("FR")) return "FR";
  if (localeToken.startsWith("DE")) return "DE";
  return "EN";
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function resolveLocalized(value, languageKey) {
  if (typeof value === "string") return value;
  if (!value || typeof value !== "object") return "";
  return value[languageKey] ?? value.EN ?? "";
}

function resolveKeyLabel(key, layoutConfig, getKeyLabel) {
  const fallbackLabels = {
    KeyZ: layoutConfig?.backwardSlowDownKey ?? "Z",
    Slash: layoutConfig?.forwardSpeedUpKey ?? "/",
    KeyS: layoutConfig?.KeyS ?? "S",
    KeyD: layoutConfig?.KeyD ?? "D",
    KeyF: layoutConfig?.KeyF ?? "F",
    KeyK: layoutConfig?.KeyK ?? "K",
    KeyL: layoutConfig?.KeyL ?? "L",
    Semicolon: layoutConfig?.Semicolon ?? ";",
    spaceKey: layoutConfig?.spaceKey ?? "Space",
  };

  const fallback = fallbackLabels[key] ?? key;
  if (typeof getKeyLabel !== "function") return fallback;

  const missionLabel = getKeyLabel(key, layoutConfig);
  if (typeof missionLabel === "string" && missionLabel.length > 0) {
    return missionLabel;
  }
  return fallback;
}

function buildDetailLayer(detail, languageKey, layoutConfig, getKeyLabel) {
  if (!detail || typeof detail !== "object") return "";

  const isKeyDetail = !!detail.key;
  const topPercent = detail.top ?? detail.topPercent ?? 50;
  const leftPercent = detail.left ?? detail.leftPercent ?? 50;
  const widthPercent = detail.widthPercent;
  const maxWidthPercent = detail.maxWidthPercent;
  const zIndex = detail.zIndex ?? (isKeyDetail ? 8 : 3);
  const commonStyle = [
    "position:absolute",
    `top:${topPercent}%`,
    `left:${leftPercent}%`,
    "transform:translate(-50%, -50%)",
    `z-index:${zIndex}`,
  ];

  if (typeof widthPercent === "number") commonStyle.push(`width:${widthPercent}%`);
  if (typeof maxWidthPercent === "number") commonStyle.push(`max-width:${maxWidthPercent}%`);

  if (detail.type === "asset" && detail.src) {
    return image({
      src: detail.src,
      alt: detail.alt || "Instruction detail",
      style: `${commonStyle.join(";")};height:auto;object-fit:contain;`,
    });
  }

  const textValue = isKeyDetail
    ? resolveKeyLabel(String(detail.key), layoutConfig, getKeyLabel)
    : resolveLocalized(detail.text, languageKey);
  if (!textValue) return "";

  const fontSizeVw = detail.fontSizeVw ?? (isKeyDetail ? 1.0 : 0.95);
  const textColor = detail.color ?? (isKeyDetail ? "#FFFFFF" : "#0A0A0A");
  const bgColor = detail.bgColor ?? "transparent";
  const paddingVw = detail.paddingVw ?? 0;
  const textAlign = detail.textAlign ?? (isKeyDetail ? "center" : "left");
  const borderRadiusVw = detail.borderRadiusVw ?? (isKeyDetail ? 0.2 : 0);
  const borderStyle = detail.border ?? "none";
  const fontWeight = detail.fontWeight ?? (isKeyDetail ? 700 : 400);
  const fontFamily = detail.fontFamily ?? (isKeyDetail ? "'Baloo Paaji 2', 'Baloo Paaji', sans-serif" : "'Inter', sans-serif");
  const textStroke = detail.textStroke ?? (isKeyDetail ? "0.25vw #A4390B" : "0");
  const paintOrder = detail.paintOrder ?? (isKeyDetail ? "stroke fill" : "normal");
  const textShadow = detail.textShadow ?? (isKeyDetail ? "none" : "none");
  const detailClassName = typeof detail.className === "string" ? detail.className : "";
  const detailAttributes = detailClassName
    ? {
      class: detailClassName,
      style: `${commonStyle.join(";")};font-family:${fontFamily};font-size:${fontSizeVw}vw;color:${textColor};background:${bgColor};padding:${paddingVw}vw;text-align:${textAlign};border:${borderStyle};border-radius:${borderRadiusVw}vw;font-weight:${fontWeight};line-height:1.15;white-space:${isKeyDetail ? "nowrap" : "pre-wrap"};-webkit-text-stroke:${textStroke};text-shadow:${textShadow};paint-order:${paintOrder};`,
    }
    : {
      style: `${commonStyle.join(";")};font-family:${fontFamily};font-size:${fontSizeVw}vw;color:${textColor};background:${bgColor};padding:${paddingVw}vw;text-align:${textAlign};border:${borderStyle};border-radius:${borderRadiusVw}vw;font-weight:${fontWeight};line-height:1.15;white-space:${isKeyDetail ? "nowrap" : "pre-wrap"};-webkit-text-stroke:${textStroke};text-shadow:${textShadow};paint-order:${paintOrder};`,
    };

  return div(escapeHtml(textValue), {
    ...detailAttributes,
  });
}

function buildDetailsLayers(details, languageKey, layoutConfig, getKeyLabel) {
  if (!Array.isArray(details) || details.length === 0) return "";
  return details
    .map((detail) => buildDetailLayer(detail, languageKey, layoutConfig, getKeyLabel))
    .join("");
}

const LINE_BREAK_TOKEN = "__GS_LINE_BREAK__";
const PARAGRAPH_BREAK_TOKEN = "__GS_PARAGRAPH_BREAK__";

function annotateLineBreaks(rawText) {
  let output = "";

  for (let i = 0; i < rawText.length; i += 1) {
    if (rawText[i] !== "\n") {
      output += rawText[i];
      continue;
    }

    // Double newline is an explicit author override for paragraph spacing.
    if (rawText[i + 1] === "\n") {
      output += PARAGRAPH_BREAK_TOKEN;
      i += 1;
      continue;
    }

    output += LINE_BREAK_TOKEN;
  }

  return output;
}

function renderAnnotatedLineBreaks(text) {
  return text
    .replace(new RegExp(PARAGRAPH_BREAK_TOKEN, "g"), '<br><span style="display:block;height:0.55em;"></span>')
    .replace(new RegExp(LINE_BREAK_TOKEN, "g"), "<br>");
}

function renderBubbleText(bubbleObj, layoutConfig, getKeyLabel) {
  let text = annotateLineBreaks(String(bubbleObj?.text ?? ""));
  if (!bubbleObj.highlights || bubbleObj.highlights.length === 0) {
    return renderAnnotatedLineBreaks(escapeHtml(text));
  }

  const highlights = [...bubbleObj.highlights]
    .filter((hl) => hl && (hl.text || hl.conditional?.key))
    .sort((a, b) => {
      const sourceA = a.conditional?.key ? `{{${a.conditional.key}}}` : String(a.text ?? "");
      const sourceB = b.conditional?.key ? `{{${b.conditional.key}}}` : String(b.text ?? "");
      return sourceB.length - sourceA.length;
    });
  highlights.forEach((hl) => {
    const sourceText = hl.conditional && hl.conditional.key
      ? `{{${hl.conditional.key}}}`
      : String(hl.text ?? "");
    const displayText = hl.conditional && hl.conditional.key
      ? resolveKeyLabel(hl.conditional.key, layoutConfig, getKeyLabel)
      : String(hl.text ?? "");
    if (!sourceText || !displayText) return;
    const escaped = sourceText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    text = text.replace(
      new RegExp(escaped, "g"),
      `<span style="color:#DE5700;font-weight:800;">${escapeHtml(displayText)}</span>`
    );
  });

  text = text.replace(
    /\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g,
    (_, key) => escapeHtml(resolveKeyLabel(key, layoutConfig, getKeyLabel))
  );

  return renderAnnotatedLineBreaks(text);
}

function buildMissionInstructionCodedPage(
  pageConfig,
  languageKey,
  audioSrc,
  layoutConfig,
  overlaySizePresets,
  bubbleDefaultLayout,
  pilotLayoutAdjustments,
  getKeyLabel
) {
  const rawContainerScale = typeof pageConfig?.scale === "function"
    ? pageConfig.scale(pageConfig)
    : pageConfig?.scale ?? 1.30;
  const containerScale = Number(rawContainerScale);
  const hasValidContainerScale = Number.isFinite(containerScale) && containerScale > 0;
  const containerScaleStyle = hasValidContainerScale
    ? `transform:scale(${containerScale});transform-origin:center center;`
    : "";

  const bubbleObj = pageConfig.bubbleText;
  const overlay = pageConfig.overlay ?? {};
  const overlayPreset = overlaySizePresets[overlay.overlayPreset] ?? overlaySizePresets.md;
  const overlayLeftPercent = typeof overlay.overlayLeftPercent === "number"
    ? overlay.overlayLeftPercent
    : overlayPreset.overlayLeftPercent + (pageConfig.showPilot ? pilotLayoutAdjustments.overlayLeftOffsetPercent : 0);
  const overlayTopPercent = typeof overlay.overlayTopPercent === "number"
    ? overlay.overlayTopPercent
    : overlayPreset.overlayTopPercent;
  const bubbleLeftPercent = typeof bubbleObj?.bubbleLeftPercent === "number"
    ? bubbleObj.bubbleLeftPercent
    : typeof overlay.bubbleLeftPercent === "number"
      ? overlay.bubbleLeftPercent
      : bubbleDefaultLayout.wrapperLeftPercent;
  const bubbleBottomPercent = typeof bubbleObj?.bubbleBottomPercent === "number"
    ? bubbleObj.bubbleBottomPercent
    : typeof overlay.bubbleBottomPercent === "number"
      ? overlay.bubbleBottomPercent
      : bubbleDefaultLayout.wrapperBottomPercent;
  const bubbleMinWidthPercent = typeof bubbleObj?.bubbleMinWidthPercent === "number"
    ? bubbleObj.bubbleMinWidthPercent
    : typeof overlay.bubbleMinWidthPercent === "number"
      ? overlay.bubbleMinWidthPercent
      : bubbleDefaultLayout.minWidthPercent;
  const bubbleMaxWidthPercent = typeof bubbleObj?.bubbleMaxWidthPercent === "number"
    ? bubbleObj.bubbleMaxWidthPercent
    : typeof overlay.bubbleMaxWidthPercent === "number"
      ? overlay.bubbleMaxWidthPercent
      : pageConfig.showPilot
        ? pilotLayoutAdjustments.bubbleMaxWidthPercent
        : bubbleDefaultLayout.maxWidthPercent;
  const screenshotLayout = {
    leftPercent: 50,
    topPercent: 10,
    widthPercent: 48,
    maxWidthPercent: 48,
    titleLeftPercent: 50,
    titleWidthPercent: 28,
    titleTopVw: -0.25,
    ...(pageConfig?.screenshot ?? {}),
  };
  const screenshotTitle = resolveLocalized(pageConfig?.screenshot?.title, languageKey);
  const detailsMarkup = buildDetailsLayers(
    pageConfig?.screenshot?.details,
    languageKey,
    layoutConfig,
    getKeyLabel
  );

  const audioTag = audioSrc
    ? audio({
      id: INSTRUCTIONS_AUDIO_ID,
      src: audioSrc,
      autoplay: true,
      style: "display:none;",
    })
    : "";

  const containerAttributes = {
    style: `position:relative;width:85%;height:85%;margin:0 auto;overflow:hidden;border-radius:0.8vw;${containerScaleStyle};`,
  };

  containerAttributes["data-overlay-left-percent"] = overlayLeftPercent;
  containerAttributes["data-overlay-top-percent"] = overlayTopPercent;
  if (overlay.overlayNavText !== undefined) {
    containerAttributes["data-overlay-nav-text"] = overlay.overlayNavText;
  }
  if (overlay.showBackKey !== undefined) {
    containerAttributes["data-overlay-show-back-key"] = String(overlay.showBackKey);
  }
  if (overlay.showForwardKey !== undefined) {
    containerAttributes["data-overlay-show-forward-key"] = String(overlay.showForwardKey);
  }
  if (overlay.overlayTextSizeVw !== undefined) {
    containerAttributes["data-overlay-text-size-vw"] = overlay.overlayTextSizeVw;
  }

  return div(
    div(
      audioTag +
      image({
        id: INSTRUCTIONS_IMAGE_ID,
        src: pageConfig.backgroundSrc,
        alt: "An image with some instructions",
        style: "width:85%;height:85%",
      }) +
      (pageConfig.fellowSrc
        ? image({
          src: pageConfig.fellowSrc,
          alt: "Headquarter fellow",
          style: "position:absolute;left:11.5%;bottom:5%;width:15%;height:auto;z-index:2;object-fit:contain;",
        })
        : "") +
      (pageConfig.pilotSrc
        && pageConfig.showPilot
        ? image({
          src: pageConfig.pilotSrc,
          alt: "Pilot",
          style: "position:absolute;right:8%;bottom:0%;width:22%;height:auto;z-index:2;object-fit:contain;",
        })
        : "") +
      div(
        div(
          renderBubbleText(bubbleObj, layoutConfig, getKeyLabel) +
          div("", {
            style: `position:absolute;bottom:${bubbleDefaultLayout.tailBottomVw}vw;left:${bubbleDefaultLayout.tailLeftVw}vw;width:${bubbleDefaultLayout.tailWidthVw}vw;height:${bubbleDefaultLayout.tailHeightVw}vw;background:#FFFFFF;clip-path:polygon(20% 0, 100% 0, 0 100%);`,
          }),
          {
            style: `position:relative;background:#FFFFFF;border:none;border-radius:0.8vw 0.8vw 0.8vw 0;padding:${bubbleDefaultLayout.padding};color:#000000;font-family:'Inter',sans-serif;font-size:${bubbleDefaultLayout.fontSizeVw}vw;font-weight:600;line-height:1.22;text-align:left;white-space:pre-wrap;`,
          }
        ),
        {
          "data-instructions-bubble": "true",
          style: `position:absolute;left:${bubbleLeftPercent}%;bottom:${bubbleBottomPercent}%;width:fit-content;max-width:${bubbleMaxWidthPercent}%;min-width:${bubbleMinWidthPercent}%;z-index:3;`,
        }
      ) +
      (pageConfig?.screenshot?.src
        ? (
          div(escapeHtml(screenshotTitle), {
            style: `position:absolute;top:${screenshotLayout.titleTopVw}vw;left:${screenshotLayout.titleLeftPercent}%;transform:translateX(-50%);width:${screenshotLayout.titleWidthPercent}%;height:12%;min-height:30px;display:flex;align-items:center;justify-content:center;background:#112A53;color:#FFFFFF;font-family:'Baloo Paaji',sans-serif;font-size:1.5vw;font-weight:700;line-height:1;text-align:center;z-index:2;`,
          }) +
          div(
            image({
              src: pageConfig.screenshot.src,
              alt: "Instruction screenshot",
              style: `position:absolute;left:${screenshotLayout.leftPercent}%;top:${screenshotLayout.topPercent}%;transform:translateX(-50%);width:${screenshotLayout.widthPercent}%;max-width:${screenshotLayout.maxWidthPercent}%;height:auto;display:block;object-fit:contain;`,
            }) + detailsMarkup,
            {
              style: "position:absolute;top:0;left:0;width:100%;height:100%;z-index:3;",
            }
          )
        )
        : (detailsMarkup
          ? div(detailsMarkup, { style: `position:absolute;top:${screenshotLayout.topPercent}%;left:${screenshotLayout.leftPercent}%;transform:translateX(-50%);width:${screenshotLayout.widthPercent}%;max-width:${screenshotLayout.maxWidthPercent}%;z-index:2;` })
          : "")),
      containerAttributes
    ),
    {
      style: "overflow-x:clip;overflow-y:visible;overscroll-behavior-x:contain;width:100%;display:flex;justify-content:center;",
    }
  );
}

/**
 * Generic mission instruction pages builder. Mission-specific files should provide
 * page data + layout constants and call this function.
 */
export function buildMissionInstructionPagesCore({
  jsPsych,
  instructionAudio,
  pages,
  overlaySizePresets,
  bubbleDefaultLayout,
  pilotLayoutAdjustments,
  getKeyLabel,
}) {
  return pages.map((pageConfig) => {
    return () => {
      const layoutConfig = getSelectedKeyboardLayoutConfig(jsPsych);
      const languageKey = resolveInstructionLanguage(layoutConfig);
      const resolvedAudioSrc = typeof instructionAudio === "function"
        ? instructionAudio(pageConfig.audioSrc)
        : pageConfig.audioSrc;

      return buildMissionInstructionCodedPage(
        pageConfig,
        languageKey,
        resolvedAudioSrc,
        layoutConfig,
        overlaySizePresets,
        bubbleDefaultLayout,
        pilotLayoutAdjustments,
        getKeyLabel
      );
    };
  });
}
